import React, { useContext, useState } from 'react';
import { Box, Button, Collapse, Flex, Icon, Spinner, Text, VStack, useToast } from '@chakra-ui/react';
import { FiChevronDown } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { Bounty, BountyClaim, BountyProposal } from '@foodfluence/shared';
import { useBountyData } from '../../../contexts/BountyDataContext';
import { InfluencerDataContext } from '../../../contexts/InfluencerDataContext';
import { bountyApi } from '../../../services/api/bounties';
import { ff } from '../../../theme';
import {
  claimRoutePath, claimStatusChip, proposalStatusChip, resubmitProposalPath, unclaimedApprovedBounties,
} from '../utils';
import { NOT_SELECTED_COPY } from '../constants';
import BrandTile from './BrandTile';
import Pill from './Pill';
import { bountyColors, colorSchemeToTone, eyebrowStyle, PillTone } from '../theme';

const RED = bountyColors.red;

/** One My Bounties row: logo, name, next-step line and status pill; extras (note, action) sit below. */
const Row: React.FC<{
  name: string;
  logoUrl?: string;
  sub: string;
  urgent?: boolean;
  pill: { label: string; tone: PillTone };
  onClick: () => void;
  busy?: boolean;
  children?: React.ReactNode;
}> = ({ name, logoUrl, sub, urgent, pill, onClick, busy, children }) => (
  <Flex direction="column" gap="10px" py="14px" borderBottom="1px solid" borderColor={ff.line} opacity={busy ? 0.6 : 1}>
    <Flex
      as="button"
      type="button"
      w="full"
      textAlign="left"
      align="center"
      gap="14px"
      cursor={busy ? 'wait' : 'pointer'}
      pointerEvents={busy ? 'none' : 'auto'}
      onClick={onClick}
    >
      <BrandTile name={name} logoUrl={logoUrl} size={44} />
      <Flex direction="column" gap="3px" flex={1} minW={0}>
        <Text fontSize="16px" fontWeight={600} color={ff.ink} noOfLines={1}>{name}</Text>
        <Text fontSize="13px" fontWeight={urgent ? 700 : 400} color={urgent ? RED : ff.text2}>{sub}</Text>
      </Flex>
      {busy ? <Spinner size="xs" color={ff.muted} /> : <Pill label={pill.label} tone={pill.tone} />}
    </Flex>
    {children && <Box ml="58px">{children}</Box>}
  </Flex>
);

const Note: React.FC<{ text: string }> = ({ text }) => (
  <Box px="12px" py="10px" borderRadius="12px" bg={bountyColors.redTint} color={bountyColors.redInk} fontSize="13px" lineHeight="1.4">
    <Text as="strong">Note from FoodFluence:</Text> {text}
  </Box>
);

const LimeAction: React.FC<{ label: string; onClick: () => void; isLoading?: boolean }> = ({ label, onClick, isLoading }) => (
  <Button variant="lime" h="44px" px="16px" borderRadius="full" fontSize="14px" isLoading={isLoading} onClick={onClick}>
    {label}
  </Button>
);

const claimSub = (claim: BountyClaim): { text: string; urgent?: boolean } => {
  switch (claim.status) {
    case 'claimed': return { text: 'Next: introduce your contact' };
    case 'introSubmitted': return { text: 'Waiting on the meeting' };
    case 'introRejected': return { text: 'Tap to add a new contact', urgent: true };
    case 'notSelected': return { text: 'Another intro converted first' };
    case 'verified':
      return claim.payoutStatus === 'paid'
        ? { text: `$${claim.payoutAmount ?? 0} sent to your Whop wallet` }
        : { text: `Meeting verified · $${claim.payoutAmount ?? 0}` };
  }
};

const MyBountiesList: React.FC = () => {
  const { bounties, proposals, claims, claimsLoading, refetchBounties } = useBountyData();
  const { influencerData } = useContext(InfluencerDataContext);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [claimingBountyId, setClaimingBountyId] = useState<string | null>(null);
  const navigate = useNavigate();
  const toast = useToast();

  const visibleApprovedBounties = unclaimedApprovedBounties(bounties, claims);
  const visibleProposals = proposals.filter(p => p.status !== 'approved');
  const logoFor = (bountyId: string) => bounties.find(b => b.id === bountyId)?.logoUrl;

  const handleClaimBounty = async (bountyId: string) => {
    if (!influencerData?.id) return;
    setClaimingBountyId(bountyId);
    try {
      const res = await bountyApi.claimBounty(influencerData.id, bountyId);
      await refetchBounties();
      navigate(`/bounties/claims/${res.claimId}/claimed`);
    } catch (error) {
      toast({
        title: 'Could not claim bounty',
        description: error instanceof Error ? error.message : 'Please try again.',
        status: 'error', duration: 5000, isClosable: true,
      });
      refetchBounties();
    } finally {
      setClaimingBountyId(null);
    }
  };

  if (claims.length === 0 && visibleProposals.length === 0 && visibleApprovedBounties.length === 0) {
    if (claimsLoading) {
      return (
        <Flex justify="center" py="24px">
          <Spinner size="sm" color={ff.muted} />
        </Flex>
      );
    }
    return (
      <Text textAlign="center" fontSize="15px" color={ff.text2} py="40px">
        Nothing here yet — claim a bounty or submit your own to get started.
      </Text>
    );
  }

  const renderProposalRow = (proposal: BountyProposal) => {
    const chip = proposalStatusChip(proposal);
    const isResubmittable = proposal.status === 'rejected';
    const formPath = isResubmittable ? resubmitProposalPath(proposal.id) : `/bounties/propose?edit=${proposal.id}`;
    return (
      <Row
        key={proposal.id}
        name={proposal.companyName}
        logoUrl={proposal.logoUrl}
        sub={isResubmittable ? 'Tap to resubmit' : 'Tap to edit'}
        pill={{ label: chip.label, tone: isResubmittable ? 'amber' : colorSchemeToTone(chip.colorScheme) }}
        onClick={() => navigate(formPath)}
      >
        {isResubmittable && proposal.rejectionNote ? <Note text={proposal.rejectionNote} /> : null}
      </Row>
    );
  };

  const renderApprovedBountyRow = (bounty: Bounty) => {
    const chip = proposalStatusChip({ status: 'approved', amount: bounty.amount });
    const isClaiming = claimingBountyId === bounty.id;
    return (
      <Row
        key={bounty.id}
        name={bounty.companyName}
        logoUrl={bounty.logoUrl}
        sub="Your proposal is now a bounty"
        pill={{ label: chip.label, tone: colorSchemeToTone(chip.colorScheme) }}
        busy={isClaiming}
        onClick={() => handleClaimBounty(bounty.id)}
      >
        <LimeAction label="Claim this bounty" isLoading={isClaiming} onClick={() => handleClaimBounty(bounty.id)} />
      </Row>
    );
  };

  const renderClaimRow = (claim: BountyClaim) => {
    const chip = claimStatusChip(claim.status);
    const isNotSelected = claim.status === 'notSelected';
    const isPaid = claim.status === 'verified' && claim.payoutStatus === 'paid';
    const needsResubmit = claim.status === 'introRejected';
    const expanded = expandedId === claim.id;
    const toggle = () => setExpandedId(expanded ? null : claim.id);
    const sub = claimSub(claim);
    return (
      <Row
        key={claim.id}
        name={claim.companyName}
        logoUrl={logoFor(claim.bountyID)}
        sub={sub.text}
        urgent={sub.urgent}
        pill={isPaid ? { label: 'Paid', tone: 'green' } : { label: chip.label, tone: colorSchemeToTone(chip.colorScheme) }}
        onClick={isNotSelected ? toggle : () => navigate(claimRoutePath(claim))}
      >
        {needsResubmit && claim.rejectionReason ? <Note text={claim.rejectionReason} /> : null}
        {claim.status === 'verified' && !isPaid ? (
          <LimeAction label="Open Wallet" onClick={() => navigate('/wallet')} />
        ) : null}
        {isNotSelected ? (
          <Box>
            <Button
              variant="unstyled"
              display="flex"
              alignItems="center"
              gap="4px"
              h="44px"
              fontSize="14px"
              fontWeight={600}
              color={ff.ink}
              aria-expanded={expanded}
              onClick={toggle}
              rightIcon={<Icon as={FiChevronDown} boxSize="16px" transition="transform 0.15s" transform={expanded ? 'rotate(180deg)' : undefined} />}
            >
              What happened?
            </Button>
            <Collapse in={expanded}>
              <Text mt="6px" px="12px" py="10px" borderRadius="12px" bg={ff.grey} fontSize="13px" lineHeight="1.4" color="#3E3E42">
                {NOT_SELECTED_COPY}
              </Text>
            </Collapse>
          </Box>
        ) : null}
      </Row>
    );
  };

  return (
    <VStack spacing={0} align="stretch">
      {(visibleProposals.length > 0 || visibleApprovedBounties.length > 0) && (
        <Box as="section">
          <Text as="h2" {...eyebrowStyle} mt="24px" mb="4px">Your proposals</Text>
          {visibleProposals.map(renderProposalRow)}
          {visibleApprovedBounties.map(renderApprovedBountyRow)}
        </Box>
      )}
      {claims.length > 0 && (
        <Box as="section">
          <Text as="h2" {...eyebrowStyle} mt="24px" mb="4px">Your claims</Text>
          {claims.map(renderClaimRow)}
        </Box>
      )}
    </VStack>
  );
};

export default MyBountiesList;
